"use client";

import { usePathname, useSearchParams } from "next/navigation";
import {
	createContext,
	type ReactNode,
	Suspense,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

type AppRoutePendingValue = {
	isRoutePending: boolean;
	/** Call immediately before a client-side navigation (e.g. `router.push`). */
	markNavigationStart: () => void;
};

const AppRoutePendingContext = createContext<AppRoutePendingValue | null>(null);

function RoutePendingClearOnUrlChange({ onClear }: { onClear: () => void }) {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const search = searchParams.toString();
	const routeKey = search ? `${pathname}?${search}` : pathname;
	const prevKey = useRef<string | null>(null);

	// RSC / client navigations: clear when pathname or search updates (e.g. Discover bento `router.push` with query only).
	useEffect(() => {
		if (prevKey.current === null) {
			prevKey.current = routeKey;
			return;
		}
		if (prevKey.current !== routeKey) {
			prevKey.current = routeKey;
			onClear();
		}
	}, [onClear, routeKey]);

	return null;
}

export function AppRoutePendingProvider({ children }: { children: ReactNode }) {
	const [isRoutePending, setIsRoutePending] = useState(false);

	const markNavigationStart = useCallback(() => {
		setIsRoutePending(true);
	}, []);

	const clearRoutePending = useCallback(() => {
		setIsRoutePending(false);
	}, []);

	// Link-based navigations to another in-app route (same tab).
	useEffect(() => {
		const onClickCapture = (e: MouseEvent) => {
			if (e.defaultPrevented || e.button !== 0) {
				return;
			}
			if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
				return;
			}
			const t = e.target;
			if (!(t instanceof Element)) {
				return;
			}
			const a = t.closest("a[href]");
			if (!a || !(a instanceof HTMLAnchorElement)) {
				return;
			}
			if (a.target && a.target !== "_self") {
				return;
			}
			if (a.hasAttribute("download")) {
				return;
			}
			const href = a.getAttribute("href");
			if (!href || href.startsWith("#")) {
				return;
			}
			if (href.startsWith("mailto:") || href.startsWith("tel:")) {
				return;
			}
			try {
				const u = new URL(a.href, window.location.href);
				if (u.origin !== window.location.origin) {
					return;
				}
				const current = new URL(window.location.href);
				// Same Next route document (hash-only / fragment navigation does not update `routeKey`).
				if (
					u.pathname === current.pathname &&
					u.search === current.search
				) {
					return;
				}
				setIsRoutePending(true);
			} catch {
				// ignore invalid href
			}
		};

		document.addEventListener("click", onClickCapture, true);
		return () => {
			document.removeEventListener("click", onClickCapture, true);
		};
	}, []);

	const value = useMemo<AppRoutePendingValue>(
		() => ({ isRoutePending, markNavigationStart }),
		[isRoutePending, markNavigationStart],
	);

	return (
		<AppRoutePendingContext.Provider value={value}>
			<Suspense fallback={null}>
				<RoutePendingClearOnUrlChange onClear={clearRoutePending} />
			</Suspense>
			{children}
		</AppRoutePendingContext.Provider>
	);
}

export function useAppRoutePending(): boolean {
	return useContext(AppRoutePendingContext)?.isRoutePending ?? false;
}

export function useMarkAppRouteNavigationStart(): () => void {
	return (
		useContext(AppRoutePendingContext)?.markNavigationStart ?? (() => {})
	);
}
